import type { AcceptsChildren, IconButtonProps, TestIdProps } from "@/theme"
import {
  Backdrop,
  Box,
  CloseIconButton,
  IconButton,
  Paper,
  PresenceTransition,
  Row,
  Theme,
  View,
  Z_INDEX,
  useBreakpointValue,
} from "@/theme"
import React from "react"
import { StyleSheet } from "react-native"

interface ProviderProps {
  onClosed?: () => void
  onOpened?: () => void
}

/**
 *
 */
function useSidebar({ onClosed, onOpened, ...rest }: ProviderProps) {
  const [isOpen, setIsOpen] = React.useState(false)

  const handleCloseSidebar = React.useCallback(() => {
    setIsOpen(false)
    if (onClosed) {
      onClosed()
    }
  }, [onClosed])
  const handleOpenSidebar = React.useCallback(() => {
    setIsOpen(true)
    if (onOpened) {
      onOpened()
    }
  }, [onOpened])
  const handleToggleSidebar = React.useCallback(() => {
    setIsOpen((prev) => !prev)
  }, [])
  const getBreakpointValue = useBreakpointValue()
  const isDocked =
    getBreakpointValue({
      base: false,
      lg: true,
      xl: true,
    }) === true
  return React.useMemo(() => {
    return {
      handleCloseSidebar,
      handleOpenSidebar,
      handleToggleSidebar,
      isDocked,
      isOpen,
      ...rest,
    }
  }, [
    handleCloseSidebar,
    handleOpenSidebar,
    handleToggleSidebar,
    isDocked,
    isOpen,
    rest,
  ])
}

type ContextValue = ReturnType<typeof useSidebar>

const Context = React.createContext<ContextValue | undefined>(undefined)

export function SidebarContextProvider({
  children,
  ...rest
}: AcceptsChildren & ProviderProps): React.JSX.Element | null {
  const value = useSidebar(rest)
  return <Context.Provider value={value}>{children}</Context.Provider>
}

export function useSidebarContext(): ContextValue {
  const ctx = React.useContext(Context)
  if (typeof ctx === "undefined") {
    throw new TypeError(
      `SidebarLayout Context must be used inside of SidebarContextProvider`,
    )
  }
  return ctx
}

export function CloseSidebarIconButton(
  props: Pick<IconButtonProps, "size" | "variant">,
): React.JSX.Element | null {
  const { handleCloseSidebar, isDocked } = useSidebarContext()
  if (isDocked) {
    return null
  }
  return (
    <Box ml={"auto"}>
      <CloseIconButton
        id={"closeSidebarBtn"}
        onPress={handleCloseSidebar}
        {...props}
      />
    </Box>
  )
}

export function SidebarToggleIconButton({
  isFixed,
  ...rest
}: Omit<IconButtonProps, "IconComponent" | "onPress"> & {
  isFixed?: boolean
}): React.JSX.Element | null {
  const { handleToggleSidebar, isDocked } = useSidebarContext()
  if (isDocked) {
    return null
  }
  return (
    <IconButton
      IconComponent={"Menu"}
      size={"lg"}
      variant={"primary"}
      {...(isFixed === true
        ? {
            bottom: "screenPadding",
            position: "fixed",
            right: "screenPadding",
          }
        : {})}
      id={"sidebarToggleBtn"}
      onPress={handleToggleSidebar}
      {...rest}
    />
  )
}
const WIDTH = 375
const TRANSITION_INITIAL = { translateX: -WIDTH }

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    zIndex: Z_INDEX.drawer - 1,
  },
  dockedPageContent: {
    flex: 2,
    height: "100%",
    zIndex: 0,
  },
  dockedSidebarContent: {
    flex: 1,
    height: "100%",
    minWidth: WIDTH + 100,
    ...Theme.SHADOWS.$2,
  },
  pageContent: {
    display: "flex",
    flex: 1,
  },
  root: {
    flex: 1,
    position: "relative",
  },
  transition: {
    bottom: 0,
    flex: 1,
    left: 0,
    maxWidth: "90%",
    position: "absolute",
    top: 0,
    width: WIDTH,
    zIndex: Z_INDEX.drawer,
  },
})

interface Props extends AcceptsChildren, TestIdProps {
  SidebarComponent: React.FC | React.JSX.Element
}

function SidebarDockedLayout({ children, id, SidebarComponent }: Props) {
  const sidebarElement =
    typeof SidebarComponent === "function" ? (
      <SidebarComponent />
    ) : (
      SidebarComponent
    )
  return (
    <Row flex={1} id={id}>
      <View id={"sidebarContent"} style={styles.dockedSidebarContent}>
        {sidebarElement}
      </View>
      <View id={"pageContent"} style={styles.dockedPageContent}>
        {children}
      </View>
    </Row>
  )
}

function SidebarDrawerLayout({
  children,
  id,
  SidebarComponent,
}: Props): React.JSX.Element {
  const { handleCloseSidebar, isOpen } = useSidebarContext()

  const sidebarElement =
    typeof SidebarComponent === "function" ? (
      <SidebarComponent />
    ) : (
      SidebarComponent
    )
  // TODO: Fix this. Maybe web only?
  return (
    <View id={id} style={styles.root}>
      {isOpen ? (
        <Backdrop style={styles.backdrop} onPress={handleCloseSidebar} />
      ) : null}
      <View id={"pageContent"} style={styles.pageContent}>
        {children}
      </View>
      <PresenceTransition
        initial={TRANSITION_INITIAL}
        style={[styles.transition, !isOpen && { pointerEvents: "none" as const }]}
        visible={isOpen}
      >
        <Paper flex={1} id={"sidebarContent"} zIndex={Z_INDEX.drawer}>
          {sidebarElement}
        </Paper>
      </PresenceTransition>
    </View>
  )
}

export function SidebarPage({ children, id, SidebarComponent }: Props) {
  const { isDocked } = useSidebarContext()
  if (isDocked) {
    return (
      <SidebarDockedLayout SidebarComponent={SidebarComponent} id={id}>
        {children}
      </SidebarDockedLayout>
    )
  }
  return (
    <SidebarDrawerLayout SidebarComponent={SidebarComponent} id={id}>
      {children}
    </SidebarDrawerLayout>
  )
}
